import React, { useCallback, useRef, useState, useEffect } from 'react';
import useAsyncEffect from 'use-async-effect';
import { isIOS, isSafari } from 'react-device-detect';

import { useConferenceState } from '@hooks/conferenceContext';
import { useDeviceChange } from '../../DevicesSettings/hooks';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';

import MillicastService from '../../../../dashboard/components/HomeBottomBar/ShareScreen/helpers';

import { IDevicePermissionState } from '@interfaces/permissions';
import { NavigatorPermissionsService } from '@helpers/permissionsService';
import { AnalyticsType } from '@interfaces/analytics';
import { useMytaverseEvent } from '@core/dashboard/providers';

export const useInitWebcamDevicesMobile = (
  isIOSChrome: boolean,
  permissionState: IDevicePermissionState | null,
  setPermissionState: React.Dispatch<
    React.SetStateAction<IDevicePermissionState | null>
  >,
) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const { activeCameraDeviceId } = useConferenceState();

  const checkPermissions = useCallback(async () => {
    const permissionData =
      await NavigatorPermissionsService.checkAndEnsurePermission('camera');

    if (permissionState !== permissionData.state) {
      setPermissionState(permissionData.state);
    }
  }, [permissionState]);

  useEffect(() => {
    if (!isIOSChrome) {
      return;
    }

    setPermissionState(null);
  }, [isIOSChrome, activeCameraDeviceId]);

  useEffect(() => {
    if (!isIOSChrome) {
      return;
    }

    const init = async () => {
      await checkPermissions();

      if (!timeoutIdRef.current) {
        timeoutIdRef.current = setTimeout(init, 1000);
      }
    };

    init();

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [isIOSChrome, checkPermissions]);
};

export const useInitWebcamDevices = (activateCameraStream = true) => {
  const [permissionState, setPermissionState] =
    useState<IDevicePermissionState | null>('granted');
  const [initializing, setInitializing] = useState(true);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const unmountedRef = useRef(false);
  const trackCameraPermissionsChange = useRef(false);

  const { activeCameraDeviceId, setActiveCameraDeviceId } =
    useConferenceState();
  const { trackEventAnalytics } = useMytaverseEvent();

  const isIOSChrome = isIOS && !isSafari;

  const startCameraStream = useCallback(async (deviceId: string) => {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        deviceId: { exact: deviceId },
      },
    });

    if (unmountedRef.current) {
      MillicastService.stopMediaStreamTracks(mediaStream);
      return;
    }

    setMediaStream(mediaStream);

    const video = videoRef.current;

    if (!video) {
      return;
    }

    video.srcObject = mediaStream;
    video.onloadedmetadata = () => {
      video.play();
    };
  }, []);

  useAsyncEffect(async () => {
    switch (permissionState) {
      case 'prompt': {
        try {
          trackCameraPermissionsChange.current = true;

          trackEventAnalytics(AnalyticsType.InputDevicePermissionsPrompt, {
            type: 'camera',
          });

          await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: true,
          });
        } catch (e) {
          console.error(e);
          setInitializing(false);
        }

        break;
      }
      case 'denied': {
        const video = videoRef.current;

        if (video) {
          video.srcObject = null;
        }

        setDevices([]);
        setMediaStream(null);
        setInitializing(false);

        if (trackCameraPermissionsChange.current) {
          trackEventAnalytics(AnalyticsType.InputDevicePermissionsDeny, {
            type: 'camera',
          });
          trackCameraPermissionsChange.current = false;
        }

        break;
      }
      case 'granted': {
        if (!initializing) {
          return;
        }

        if (!navigator.mediaDevices?.enumerateDevices) {
          throw new Error('enumerateDevices() not supported.');
        }

        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cameraDevices = (devices || []).filter(
            (device) => device.kind === 'videoinput',
          );

          localStorage.setItem('cameraDevices', JSON.stringify(cameraDevices));

          const activeDeviceId =
            cameraDevices.find(
              ({ deviceId }) => deviceId === activeCameraDeviceId,
            )?.deviceId || cameraDevices[0]?.deviceId;

          setDevices(cameraDevices);
          setInitializing(false);

          if (trackCameraPermissionsChange.current) {
            trackEventAnalytics(AnalyticsType.InputDevicePermissionsAccept, {
              type: 'camera',
            });
            trackCameraPermissionsChange.current = false;
          }

          if (activeDeviceId && activateCameraStream) {
            setActiveCameraDeviceId(activeDeviceId);
            await startCameraStream(activeDeviceId);
          }
        } catch (error) {
          setInitializing(false);
          throw Error(getCatchErrorMessage(error));
        }
        break;
      }
    }
  }, [
    permissionState,
    activeCameraDeviceId,
    initializing,
    setActiveCameraDeviceId,
    startCameraStream,
    trackEventAnalytics,
  ]);

  useInitWebcamDevicesMobile(isIOSChrome, permissionState, setPermissionState);

  useEffect(() => {
    setInitializing(true);
  }, [activeCameraDeviceId]);

  useDeviceChange(setInitializing);

  useAsyncEffect(async () => {
    if (isIOSChrome) {
      return;
    }

    try {
      const permissionData =
        await NavigatorPermissionsService.checkAndEnsurePermission('camera');

      setPermissionState(permissionData.state);

      if ('onchange' in permissionData) {
        permissionData.onchange = () => {
          trackCameraPermissionsChange.current = true;
          setInitializing(true);
          setPermissionState(permissionData.state);
        };
      }
    } catch (error) {
      setPermissionState('denied');
      MytaverseLogger.error(getCatchErrorMessage(error));
    }
  }, [isIOSChrome]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (mediaStream) {
        MillicastService.stopMediaStreamTracks(mediaStream);
      }
    };
  }, [mediaStream]);

  return {
    initializing,
    devices,
    videoRef,
    startCameraStream,
  };
};
